import React from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  useActiveSettlementMethodQuery,
  useAutoDebitRegistrationQuery,
  useDeleteAutoDebitRegistrationMutation,
} from 'apollo/queries/autogenerate/hooks';
import IMAGES from 'shared/constants/images';
import Button from 'shared/modules/common/components/atoms/Button';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import colors from 'shared/modules/common/constants/colors';

type Props = {
  autoDebitId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isAutoDebitActive: boolean;
  billingCycle: string;
};

export default function ({
  autoDebitId,
  open,
  onClose,
  onSuccess,
  isAutoDebitActive,
  billingCycle,
}: Props) {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [deleteAutoDebit] = useDeleteAutoDebitRegistrationMutation();

  const {
    refetch: refetchActiveSettlementMethod,
  } = useActiveSettlementMethodQuery({
    variables: {
      billingCycle,
    },
    fetchPolicy: 'cache-and-network',
    skip: billingCycle === '',
  });

  const {
    refetch: refetchAutoDebitRegistration,
  } = useAutoDebitRegistrationQuery({
    fetchPolicy: 'network-only',
  });

  function getText() {
    if (isAutoDebitActive) {
      return '既に確定済みの前月ご利用分のご請求は、ご登録いただいていた銀行口座から引き落とされます。口座情報を削除した場合、翌月以降に確定するご請求のお支払い方法は、自動的に「コンビニ/銀行振込」に変更されます。';
    }

    return '削除後、お支払い方法は自動的に「コンビニ／銀行振込」へと変更されます';
  }

  async function onDeleteButtonPress() {
    await deleteAutoDebit({
      variables: {
        id: autoDebitId,
      },
    });

    refetchActiveSettlementMethod();
    refetchAutoDebitRegistration();

    onSuccess();
  }

  const onCancelButtonPress = () => {
    onClose();
  };

  return (
    <Dialog
      title=""
      open={open}
      onClose={onClose}
      onCloseButtonPress={onClose}
      showCloseButton={true}
      style={style.dialog}
    >
      <View style={[style.root, isBigScreen && style.rootBigScreen]}>
        <View style={style.topContainer}>
          <Image source={IMAGES.exclaimTriangle} style={style.headerImage} />
          <Text style={style.titleText}>ご登録の口座情報を削除します</Text>
        </View>
        <View style={style.textContainer}>
          <Text style={[style.text, style.content]}>{getText()}</Text>
        </View>

        <View
          style={[
            style.buttonContainer,
            isBigScreen && style.buttonGroupBigScreen,
          ]}
        >
          <Button
            variant="primary"
            title="削除"
            onPress={onDeleteButtonPress}
            style={[
              style.button,
              style.mgB16,
              isBigScreen && style.buttonBigScreen,
            ]}
          />
          <Button
            variant="outlined"
            title="キャンセル"
            onPress={onCancelButtonPress}
            style={[style.button, isBigScreen && style.buttonBigScreen]}
          />
        </View>
      </View>
    </Dialog>
  );
}

const style = StyleSheet.create({
  dialog: {
    maxHeight: 624,
  },
  root: {
    flex: 1,
  },
  topContainer: {
    flex: 3,
  },
  textContainer: {
    flex: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rootBigScreen: {
    marginBottom: 0,
  },
  text: {
    fontSize: 13,
    color: colors.blackPaidy,
  },
  content: {
    marginBottom: 24,
    textAlign: 'center',
  },
  buttonGroupBigScreen: {
    flexDirection: 'row',
    marginHorizontal: -8,
    justifyContent: 'center',
  },
  button: {
    maxWidth: 296,
    alignSelf: 'center',
  },
  buttonBigScreen: {
    marginHorizontal: 8,
    marginBottom: 0,
    width: '45%',
  },
  headerImage: {
    width: 184,
    height: 184,
    alignSelf: 'center',
  },
  titleText: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 24,
    textAlign: 'center',
  },
  buttonContainer: {
    flex: 1.5,
    alignItems: 'center',
  },
  mgB16: {
    marginBottom: 16,
  },
});
